import './host'

import { MembershipService } from '@graft/nuget-gc.webportals.memberships'

import { getSessionToken } from '@common/core/helpers'
import { isVisionLocal } from '@common/core/utils/isVisionLocal'

import { invitationToObject } from './objects'
import type { Invitation } from './types/invitation.type'
import { isMembershipsServiceConfigured } from './utils/isMembershipsServiceConfigured'

async function assertMembershipSuccess(success: boolean, errorMessage: string): Promise<void> {
  if (!success) {
    throw new Error(errorMessage)
  }
}

export async function getInvitations(): Promise<Invitation[]> {
  try {
    if (isVisionLocal() || !isMembershipsServiceConfigured()) {
      return []
    }

    const token = await getSessionToken()
    const invitations = await MembershipService.getInvitations(token)

    if (!invitations?.length) {
      return []
    }

    return Promise.all(invitations.map((invitation) => invitationToObject(invitation)))
  } catch (error) {
    console.error('Failed to get invitations:', error)
    return []
  }
}

export async function acceptInvitation(invitationId: string): Promise<void> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return
  }

  try {
    const token = await getSessionToken()
    await assertMembershipSuccess(
      await MembershipService.acceptInvitation(token, invitationId),
      'Failed to accept invitation'
    )
  } catch (error) {
    console.error('Failed to accept invitation:', error)
    throw error
  }
}

export async function inviteWorkspaceMember(
  workspaceId: string,
  membershipLevel: string,
  email: string,
  region: string
): Promise<void> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return
  }

  try {
    const token = await getSessionToken()
    await assertMembershipSuccess(
      await MembershipService.inviteWorkspaceMember(token, workspaceId, membershipLevel, email, region),
      'Failed to invite workspace member'
    )
  } catch (error) {
    console.error('Failed to invite workspace member:', error)
    throw error
  }
}

export async function inviteProjectMember(
  projectId: string,
  membershipLevel: string,
  email: string,
  region: string
): Promise<void> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return
  }

  try {
    const token = await getSessionToken()
    await assertMembershipSuccess(
      await MembershipService.inviteProjectMember(token, projectId, membershipLevel, email, region),
      'Failed to invite project member'
    )
  } catch (error) {
    console.error('Failed to invite project member:', error)
    throw error
  }
}

export async function deleteWorkspaceMembership(workspaceId: string, deletedUserId: string): Promise<void> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return
  }

  try {
    const token = await getSessionToken()
    await assertMembershipSuccess(
      await MembershipService.deleteWorkspaceMembership(token, workspaceId, deletedUserId),
      'Failed to remove workspace member'
    )
  } catch (error) {
    console.error('Failed to remove workspace member:', error)
    throw error
  }
}

export async function deleteProjectMembership(projectId: string, deletedUserId: string): Promise<void> {
  if (isVisionLocal() || !isMembershipsServiceConfigured()) {
    return
  }

  try {
    const token = await getSessionToken()
    await assertMembershipSuccess(
      await MembershipService.deleteProjectMembership(token, projectId, deletedUserId),
      'Failed to remove project member'
    )
  } catch (error) {
    console.error('Failed to remove project member:', error)
    throw error
  }
}
